import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useImportWithMapping } from '../../hooks/useImportWithMapping';
import type { ImportServiceAdapter, ImportWithMappingResult, MappingColumnConfig } from '../../types/import';

const STEPS = ['Upload', 'Mapeamento'];

export interface ImportWithMappingDialogProps {
  open: boolean;
  onClose: () => void;
  onImportComplete: (result?: ImportWithMappingResult) => void;
  title: string;
  description: string;
  entityLabel: string;
  templateFilename: string;
  adapter: ImportServiceAdapter;
  columns: MappingColumnConfig[];
  columnLabels: Record<string, string>;
  getImportOptions: () => Record<string, unknown>;
  canSubmit?: boolean;
  scopeSelector?: React.ReactNode;
  /** Chamado quando o usuário seleciona um arquivo, antes de qualquer preview/import. */
  onFileSelected?: (file: File) => void;
  /** Valor do atributo `accept` do input de arquivo. Default: só planilha (`.xlsx,.xls`). */
  acceptedFileTypes?: string;
  /** Texto de ajuda mostrado abaixo da área de upload. Default: "Formatos aceitos: .xlsx, .xls". */
  acceptedFormatsLabel?: string;
}

const ImportWithMappingDialog: React.FC<ImportWithMappingDialogProps> = ({
  open,
  onClose,
  onImportComplete,
  title,
  description,
  entityLabel,
  templateFilename,
  adapter,
  columns,
  columnLabels,
  getImportOptions,
  canSubmit = true,
  scopeSelector,
  onFileSelected,
  acceptedFileTypes = '.xlsx,.xls',
  acceptedFormatsLabel = 'Formatos aceitos: .xlsx, .xls',
}) => {
  const {
    activeStep,
    file,
    loading,
    templateLoading,
    error,
    preview,
    mappings,
    fileInputRef,
    setError,
    handleFileChange,
    handleDownloadTemplate,
    handlePreview,
    handleConfirmImport,
    setMappingValue,
    allRequiredMapped,
    handleClose,
  } = useImportWithMapping({
    adapter,
    columns,
    getOptions: getImportOptions,
    entityLabel,
    templateFilename,
    onImportComplete,
    onFileSelected,
  });

  const onDialogClose = () => {
    handleClose();
    onClose();
  };

  const onPreviewClick = async () => {
    const result = await handlePreview();
    if (result?.shouldClose) {
      onDialogClose();
    }
  };

  const onConfirmClick = async () => {
    const result = await handleConfirmImport();
    if (result?.shouldClose) {
      onDialogClose();
    }
  };

  return (
    <Dialog open={open} onClose={onDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon />
          <Typography variant="h6">{title}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {description}
            </Typography>

            {scopeSelector}

            <Button
              variant="outlined"
              startIcon={templateLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleDownloadTemplate}
              disabled={templateLoading}
              sx={{ mb: 3 }}
              fullWidth
            >
              {templateLoading ? 'Baixando...' : 'Baixar Template de Importação'}
            </Button>

            <Box
              sx={{
                border: '2px dashed', borderColor: file ? 'primary.main' : 'grey.400',
                borderRadius: 1, p: 3, textAlign: 'center', mb: 2, cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1">{file ? file.name : 'Clique para selecionar um arquivo'}</Typography>
              <Typography variant="caption" color="text.secondary">{acceptedFormatsLabel}</Typography>
              <input ref={fileInputRef} type="file" accept={acceptedFileTypes} onChange={handleFileChange} style={{ display: 'none' }} />
            </Box>
          </>
        )}

        {activeStep === 1 && preview && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Os termos abaixo não foram encontrados automaticamente. Selecione o valor correspondente para cada um. Campos obrigatórios (*) devem ser mapeados para prosseguir.
            </Alert>

            {preview.errors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {preview.errors.length} linha(s) com erros de validação (serão ignoradas na importação).
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {preview.totalRows} linha(s) encontrada(s), {preview.validRows} válida(s).
            </Typography>

            {preview.unresolvedTerms.map((term) => {
              const options = preview.availableOptions[term.column] ?? [];
              return (
                <Box key={`${term.column}-${term.term}`} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {columnLabels[term.column] ?? term.column}{term.required ? ' *' : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip label={term.term} size="small" variant="outlined" />
                    <Typography variant="caption" color="text.secondary">
                      ({term.count} ocorrência{term.count > 1 ? 's' : ''})
                    </Typography>
                  </Box>
                  <Autocomplete
                    size="small"
                    options={options}
                    getOptionLabel={(option) => option.label}
                    value={options.find(o => o.value === (mappings[term.column]?.[term.term] ?? '')) ?? null}
                    onChange={(_, newValue) => setMappingValue(term.column, term.term, newValue?.value ?? '')}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Selecione..." />
                    )}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                  />
                </Box>
              );
            })}
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setError(null)}>
            <Box sx={{ whiteSpace: 'pre-line' }}>{error}</Box>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onDialogClose} disabled={loading}>
          Cancelar
        </Button>

        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={onPreviewClick}
            disabled={loading || !file || !canSubmit}
            startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {loading ? 'Processando...' : 'Importar'}
          </Button>
        )}

        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={onConfirmClick}
            disabled={loading || !allRequiredMapped()}
            startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {loading ? 'Importando...' : 'Confirmar Importação'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportWithMappingDialog;
